<template>
    <div class="exposure-log-page">
        <div class="page_button_header">
            <router-link :to="`/dish/${dishId}`" class="button back">
                <font-awesome-icon :icon="['fas', 'chevron-left']" class="fa-xs" />
                Back
            </router-link>
            <button v-if="log" id="edit_log_button" @click="toggleEditExposureLogForm">
                <font-awesome-icon :icon="['fas', 'pen']" />
            </button>
        </div>
        <template v-if="log">
            <h1>Exposure Log</h1>
            <div class="log_stat_item_list">
                <div class="log_stat_item">
                    <p class="medium">Date</p>
                    <div>
                        <div class="icon">
                            <font-awesome-icon :icon="['fal', 'calendar-days']" class="fa-lg" />
                        </div>
                        <p>{{ log.date }}</p>
                    </div>
                </div>
                <div class="log_stat_item">
                    <p class="medium">Anxiety Level</p>
                    <div>
                        <div class="icon">
                            <font-awesome-icon :icon="['fal', 'face-tired']" class="fa-lg" />
                        </div>
                        <p>{{ log.anxiety_level }}/10</p>
                    </div>
                </div>
                <div class="log_stat_item">
                    <p class="medium">Taste</p>
                    <div>
                        <div class="icon">
                            <font-awesome-icon :icon="['fal', 'hat-chef']" class="fa-lg" />
                        </div>
                        <p>{{ log.taste }}</p>
                    </div>
                </div>
                <div class="log_stat_item">
                    <p class="medium">Bodys Reaction</p>
                    <p class="log-descriprion">{{ log.bodys_reaction }}</p>
                </div>
                <div class="log_stat_item">
                    <p class="medium">Additional Notes</p>
                    <p class="log-descriprion">{{ log.additional_notes }}</p>
                </div>
            </div>
            <EditExposureLogForm @logEdited="logEdited" @hideForm="toggleEditExposureLogForm" :dishId="dishId" :logId="log_id" :show="showEditExposureLogForm" :date="log.date" :anxiety_level="log.anxiety_level" :taste="log.taste" :bodys_reaction="log.bodys_reaction" :additional_notes="log.additional_notes" />
        </template>
        <template v-else>
            <div v-if="loading" class="loader"></div>
            <div v-if="!log && fetchError" class="error-message">
                <p>{{ fetchError }}</p>
                <button @click="fetchExposureLog(id)">Retry</button>
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex';
import { fetchWithAuth } from '@/utils/api';
import { type ExposureLog } from '@/types';
import EditExposureLogForm from '@/components/EditExposureLogForm.vue';

const store = useStore();
const apiUrl = store.getters.getApiUrl;
const token = store.state.idToken;

// Get the ID from the URL
const route = useRoute()
const log_id = Number(route.params.log_id);
const log = ref<ExposureLog | null>(null);
const showEditExposureLogForm = ref(false);
const loading = ref(false);
const fetchError = ref<string | null>(null);

const dishId = ref(Number(route.params.dish_id));

const logEdited = (newLogData: ExposureLog) => {

    if (log.value) {

        log.value.date = newLogData.date;
        log.value.taste = newLogData.taste;
        log.value.anxiety_level = newLogData.anxiety_level;
        log.value.bodys_reaction = newLogData.bodys_reaction;
        log.value.additional_notes = newLogData.additional_notes;

    }

}

const toggleEditExposureLogForm = () => {
    showEditExposureLogForm.value = !showEditExposureLogForm.value;
}

const fetchExposureLog = async (id: number) => {

    loading.value = true;
    fetchError.value = null;

    try {
        const response = await fetchWithAuth(apiUrl + `/api/logs/${id}`, {
            method: 'GET'
        }, store);
        if (response.ok) {
            const data = await response.json();
            log.value = data;
        } else {
            fetchError.value = `Failed to fetch exposure log!`;
            console.error('Failed to fetch exposure log:', await response.text());
        }
        loading.value = false;
    } catch (error) {
        loading.value = false;
        fetchError.value = `Error fetching exposure log: ${error instanceof Error ? error.message : 'Unknown error'}`;
        // Log the error to the console
        console.error('Error fetching exposure log:', error);
    }
}

onMounted(() => {
    if ( log_id ) {
        fetchExposureLog(log_id)
    }
})
</script>

<style scoped>

    .page_button_header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-bottom: 1px solid hsl(31, 19%, 87%);
        padding-bottom: var(--space-sm);
    }

    .page_button_header button {
        cursor: pointer;
    }

    #edit_log_button {
        background-color: transparent;
        border: none;
        color: #f09943;
        padding: 10px;
        border-radius: 5px;
    }

    #edit_log_button:hover {
        background-color: #fff5ea;
    }

    .log_stat_item {
        display: flex;
        flex-direction: column;
        gap: 10px;
    }

    .log_stat_item > div {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 10px;
    }

    .icon {
      width: 50px;
      height: 50px;
      background-color: #f2ece6;
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .log_stat_item_list {
        display: flex;
        flex-direction: column;
        gap: 40px;
    }

    .log-descriprion {
        font-weight: 300;
    }

    .exposure-log-page .loader {
        margin-top: 20px;
    }

</style>
